import {
  AmenitiesAdaAdaIcon,
  AmenitiesPetsPetFriendlyIcon,
  Button,
  Divider,
  EButtonColorVariant,
  EHeadingStyleVariant,
  ETextStyleVariant,
  Heading,
  LinkButton,
  Modal,
  NumberInput,
  Text,
  Toggle,
} from '@outdoorsyco/bonfire';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { IHeaderSearchFilterGuests } from '@/redux/modules/globalHeader';
import { OptimizelyFlags, useExperimentIsEnabled } from '@/services/experiments';

type TMobileSearchGuestsModalContentProps = {
  onCloseHandler: () => void;
  guests?: Partial<IHeaderSearchFilterGuests>;
  onChangeGuests?: (guests: IHeaderSearchFilterGuests) => void;
  isGuestOccupancyAvailable?: boolean;
};

export const MobileSearchGuestsModalContent = ({
  onCloseHandler,
  guests,
  onChangeGuests,
  isGuestOccupancyAvailable,
}: TMobileSearchGuestsModalContentProps) => {
  const intl = useIntl();
  const isRentalsVDeliverySearchModeEnabled = useExperimentIsEnabled(
    OptimizelyFlags.RENTAL_V_DELIVERY_SEARCH_MODE,
  );

  const [adultsValue, setAdultsValue] = useState(guests?.adults || 1);
  const [childrenValue, setChildrenValue] = useState(guests?.children || 0);
  const [infantsValue, setInfantsValue] = useState(guests?.infants || 0);
  const [petFriendlyValue, setPetFriendlyValue] = useState(guests?.pet_friendly || false);
  const [accessibleValue, setAccessibleValue] = useState(guests?.accessible || false);

  const onResetHandler = () => {
    setAdultsValue(1);
    setChildrenValue(0);
    setInfantsValue(0);
    setPetFriendlyValue(false);
    setAccessibleValue(false);
  };

  const onConfirmHandler = () => {
    const newGuests = {
      adults: adultsValue || undefined,
      children: childrenValue || undefined,
      ...(isGuestOccupancyAvailable && { infants: infantsValue || undefined }),
      pet_friendly: petFriendlyValue || undefined,
      accessible: accessibleValue || undefined,
      seatbelts: guests?.seatbelts || undefined,
    };
    onChangeGuests?.(newGuests);
    onCloseHandler();
  };

  const getContent = () => {
    if (!isRentalsVDeliverySearchModeEnabled) {
      return (
        <div className="flex flex-col p-0 pointer-events-auto md:p-4 gap-4">
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <Text variant={ETextStyleVariant.MediumBold}>
                <FormattedMessage defaultMessage="Adults" id="rePX4c" />
              </Text>
              <Text variant={ETextStyleVariant.SmallRegular} className="text-gray-50">
                <FormattedMessage defaultMessage="Ages 13 or above" id="7GtkzA" />
              </Text>
            </div>
            <NumberInput
              ariaLabel={intl.formatMessage({
                defaultMessage: 'Adults number input',
                id: 'QI9jVh',
              })}
              value={adultsValue}
              onChange={setAdultsValue}
              minValue={1}
              maxValue={10}
            />
          </div>
          <Divider />
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <Text variant={ETextStyleVariant.MediumBold}>
                <FormattedMessage defaultMessage="Children" id="kzlY5R" />
              </Text>
              <Text variant={ETextStyleVariant.SmallRegular} className="text-gray-50">
                {isGuestOccupancyAvailable ? (
                  <FormattedMessage defaultMessage="Ages 3-12" id="cn4PHX" />
                ) : (
                  <FormattedMessage defaultMessage="Ages 12 or under" id="SAdssW" />
                )}
              </Text>
            </div>
            <NumberInput
              ariaLabel={intl.formatMessage({
                defaultMessage: 'Children number input',
                id: 'ElS1Or',
              })}
              value={childrenValue}
              onChange={setChildrenValue}
              maxValue={10}
            />
          </div>
          <Divider />
          {isGuestOccupancyAvailable && (
            <>
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <Text variant={ETextStyleVariant.MediumBold}>
                    <FormattedMessage defaultMessage="Infants" id="wHlQeN" />
                  </Text>
                  <Text variant={ETextStyleVariant.SmallRegular} className="text-gray-50">
                    <FormattedMessage defaultMessage="Ages 2 or under" id="/cJQwH" />
                  </Text>
                </div>
                <NumberInput
                  ariaLabel={intl.formatMessage({
                    defaultMessage: 'Infants number input',
                    id: 'EBh6e/',
                  })}
                  value={infantsValue}
                  onChange={setInfantsValue}
                  maxValue={10}
                />
              </div>
              <Divider />
            </>
          )}
          <div className="flex items-center justify-between my-2">
            <div className="flex items-center gap-2">
              <AmenitiesPetsPetFriendlyIcon className="text-xl font-medium" />
              <Text variant={ETextStyleVariant.MediumBold}>
                <FormattedMessage defaultMessage="Pet friendly" id="c8hn0D" />
              </Text>
            </div>
            <Toggle
              isSelected={petFriendlyValue}
              ariaLabel={intl.formatMessage({
                defaultMessage: 'Pet friendly toggle',
                id: 'gOLubl',
              })}
              onChange={setPetFriendlyValue}
            />
          </div>
          <Divider />
          <div className="flex items-center justify-between my-2">
            <div className="flex items-center gap-2">
              <AmenitiesAdaAdaIcon className="text-xl font-medium" />
              <Text variant={ETextStyleVariant.MediumBold}>
                <FormattedMessage defaultMessage="ADA accessible" id="MlZmsn" />
              </Text>
            </div>
            <Toggle
              isSelected={accessibleValue}
              ariaLabel={intl.formatMessage({
                defaultMessage: 'ADA accessible toggle',
                id: 'R6Zaoq',
              })}
              onChange={setAccessibleValue}
            />
          </div>
        </div>
      );
    }
    return (
      <div className="flex flex-col p-0 pointer-events-auto md:p-4 gap-4">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <Text variant={ETextStyleVariant.MediumBold}>
              <FormattedMessage defaultMessage="Adults" id="rePX4c" />
            </Text>
            <Text variant={ETextStyleVariant.SmallRegular} className="text-gray-50">
              <FormattedMessage defaultMessage="Ages 13 or above" id="7GtkzA" />
            </Text>
          </div>
          <NumberInput
            ariaLabel={intl.formatMessage({
              defaultMessage: 'Adults number input',
              id: 'QI9jVh',
            })}
            value={adultsValue}
            onChange={setAdultsValue}
            minValue={1}
            maxValue={10}
          />
        </div>
        <Divider />
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <Text variant={ETextStyleVariant.MediumBold}>
              <FormattedMessage defaultMessage="Children" id="kzlY5R" />
            </Text>
            <Text variant={ETextStyleVariant.SmallRegular} className="text-gray-50">
              {isGuestOccupancyAvailable ? (
                <FormattedMessage defaultMessage="Ages 3-12" id="cn4PHX" />
              ) : (
                <FormattedMessage defaultMessage="Ages 12 or under" id="SAdssW" />
              )}
            </Text>
          </div>
          <NumberInput
            ariaLabel={intl.formatMessage({
              defaultMessage: 'Children number input',
              id: 'ElS1Or',
            })}
            value={childrenValue}
            onChange={setChildrenValue}
            maxValue={10}
          />
        </div>
        {isGuestOccupancyAvailable && (
          <>
            <Divider />
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <Text variant={ETextStyleVariant.MediumBold}>
                  <FormattedMessage defaultMessage="Infants" id="wHlQeN" />
                </Text>
                <Text variant={ETextStyleVariant.SmallRegular} className="text-gray-50">
                  <FormattedMessage defaultMessage="Ages 2 or under" id="/cJQwH" />
                </Text>
              </div>
              <NumberInput
                ariaLabel={intl.formatMessage({
                  defaultMessage: 'Infants number input',
                  id: 'EBh6e/',
                })}
                value={infantsValue}
                onChange={setInfantsValue}
                maxValue={10}
              />
            </div>
          </>
        )}
        <Divider />
        <div className="flex items-center justify-between my-2">
          <div className="flex items-center gap-2">
            <AmenitiesPetsPetFriendlyIcon className="text-xl font-medium" />
            <Text variant={ETextStyleVariant.MediumBold}>
              <FormattedMessage defaultMessage="Pet friendly" id="c8hn0D" />
            </Text>
          </div>
          <Toggle
            isSelected={petFriendlyValue}
            ariaLabel={intl.formatMessage({
              defaultMessage: 'Pet friendly toggle',
              id: 'gOLubl',
            })}
            onChange={setPetFriendlyValue}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <Modal.Actions className="top-4" />
      <Modal.Header inlineHeader={true} className="flex items-center gap-1 !p-4">
        <Heading variant={EHeadingStyleVariant.H6}>
          <FormattedMessage defaultMessage="Guests" id="NIVTxd" />
        </Heading>
      </Modal.Header>
      <Modal.Divider />
      <Modal.Content className="h-full !mb-0 !my-4 !px-4">{getContent()}</Modal.Content>
      <Divider />
      <Modal.Footer className="p-0">
        <div className="w-full">
          <div className="flex items-center justify-between p-4">
            <LinkButton
              label={intl.formatMessage({ defaultMessage: 'Clear', id: '/GCoTA' })}
              onClick={() => onResetHandler()}
            />
            <Button
              variant={EButtonColorVariant.Primary}
              label={intl.formatMessage({ defaultMessage: 'Done', id: 'JXdbo8' })}
              onClick={onConfirmHandler}
            />
          </div>
        </div>
      </Modal.Footer>
    </>
  );
};
